import rp, { helpers } from "@reporting/rp-app-host";
import { msg } from "@omegagrid/localize";
import { ContextData, ReportContext } from "@reporting/rp-types/analytics";
import { CustomDetailDefinition } from "@reporting/rp-types/definition";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { AnalyticsApplication, getDefaultSections } from "@reporting/rp-mf-analytics";
import { getPeriodDimensions, isPeriodDimension } from "../../common";
import { ModifyContextParams } from "../../types";

const app = () => rp.applications.get('analytics') as AnalyticsApplication;

/**
 * Drill down helpers shared by the visualization controllers (table, chart).
 * A drill opens a new report of the current context modified by the params -
 * grouped by a picked dimension or filtered down to the transaction detail
 * (see createReport in factories, the params travel hex encoded in the
 * component id).
 */

/** dimension tree of the group by menu - period, dimensions and their properties */
export const getDimensionMenuItems = (module: ModuleCode) => {
	return [{
		k: 'P',
		v: msg('Period'),
		icon: getDefaultSections().get('period').icon,
		level: 0,
		collapsed: true
	}, ...app().modules.get(module).dimensions.flatMap(d => ([{
		k: d.dim_num,
		v: d.description,
		icon: getDefaultSections().get('dimensions').icon,
		level: 0,
		collapsed: true
	}, ...(d.properties || []).map(a => ({
		k: `${d.dim_num}:${a.key}`,
		v: a.name,
		level: 1
	}))]))];
}

/** name of a group by dimension shown in the drill caption */
export const getDimensionName = (module: ModuleCode, dim: string): string => {
	if (isPeriodDimension(dim)) {
		return getPeriodDimensions(module).find(p => p.dim_num == dim)?.description ?? msg('Period');
	}
	return (app().modules.get(module)?.dimensions || []).find(d => d.dim_num === dim)?.description ?? null;
}

/** custom drill definitions (extension resources) applicable to the context -
 * the ones of its module, in finance narrowed down to its statement */
export const getCustomDetails = (context: ReportContext): CustomDetailDefinition[] => {
	const contextData = context.context as ContextData;
	const details = app().modules.get(contextData?.module as ModuleCode)?.custom_details ?? [];
	return details.filter(d => d.id_custom_resource != null
		&& (!d.id_statement || d.id_statement == contextData.id_statement));
}

/** opens a custom drill - the extension resource in a drill dialog over the
 * context narrowed down by the params (typically the clicked cell's filters) */
export const openCustomDrill = (context: ReportContext, detail: CustomDetailDefinition, params: ModifyContextParams, description?: string) => {
	params.view = `custom-${detail.id_custom_resource}`;
	const caption = description ? `${detail.name} › ${description}` : detail.name;
	openDrillDialog(getDrillContainerId(context, params), caption);
}

/** component id of the drill container rendering the modified context */
export const getDrillContainerId = (context: ReportContext, params: ModifyContextParams): string => {
	// TODO: workaround for id_context = 0, find better solution
	let idContext = context.id_context;
	if (!idContext) {
		app().contextBroker.currentContext = context;
		idContext = -1;
	}
	return `analytics-reportContainer-${idContext}-${helpers.hexEncode(params)}`;
}

/** drill dialog with the standard sizing */
export const openDrillDialog = (id: string, caption: string) => {
	rp.host.openDialog({
		id: id,
		caption: caption,
		closable: true,
		width: Math.min(800, rp.host.offsetWidth * 0.8),
		height: Math.min(600, rp.host.offsetHeight * 0.8),
		resizable: true,
		location: 'center',
	});
}
